import { UIHeading5, UIText2, UIText1, Image, OnboardingTooltip } from '@cian/ui-kit';
import { IconActionReload16, IconSocialLikeOff16, IconSocialLikeOn16 } from '@cian/ui-kit-design-tokens/icons';
import classNames from 'clsx';
import * as React from 'react';

import * as styles from './OfferMessage.css';

import avatar from './assets/avatar.png';
import { TChatbotReaction } from '../../types/chatbot';
import { TExpertButtonContent } from '../../types/expertButtonContent';
import { OfferGallery } from '../OfferGallery/OfferGallery';

type TOfferMessageProps = {
  isOutcoming: boolean;
  isChatbotChat: boolean;
  isExpertButtonShow?: boolean;
  withReload: boolean;
  formattedAddress: string;
  formattedInfo: string;
  formattedPrice: string;
  formattedOldPrice?: string | null;
  formattedText: React.ReactNode | null;
  shareUrl: string;
  reaction?: TChatbotReaction;
  photoUrl?: string[];
  expertButtonContent: TExpertButtonContent | null;
  messageRef: React.RefObject<HTMLDivElement | null>;
  visibleTooltip: boolean;
  tooltipPortal: React.RefObject<HTMLDivElement | null>;
  onReload(): void;
  onClick(): void;
  onLike(): void;
  setCloseTooltip(): void;
  onExpertButton(): void;
};

export const OfferMessage: React.FC<TOfferMessageProps> = ({
  isOutcoming,
  isChatbotChat,
  isExpertButtonShow,
  withReload,
  formattedAddress,
  formattedInfo,
  formattedPrice,
  formattedOldPrice,
  formattedText,
  shareUrl,
  photoUrl,
  reaction,
  expertButtonContent,
  messageRef,
  visibleTooltip,
  tooltipPortal,
  onReload,
  onClick,
  onLike,
  setCloseTooltip,
  onExpertButton,
}) => {
  const isChatbotReplay = isOutcoming && isChatbotChat;

  const handleMouseDown = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Не выполнять событие если клик произошел не колесиком или левой кнопкой
    if (event.button !== 0 && event.button !== 1) {
      return;
    }
    // Для клика левой кнопкой мыши и колесиком
    if (onClick) {
      onClick();
    }
  };

  return (
    <div
      ref={messageRef}
      className={classNames(
        styles['container'],
        withReload && !photoUrl && styles['container-short'],
        photoUrl?.length === 0 && styles['container-only-text'],
        isChatbotReplay && styles['container-replay'],
      )}
      data-name={`OfferMessage_${isOutcoming ? 'outcoming' : 'incoming'}`}
    >
      <div className={isChatbotReplay ? styles['replay-block'] : ''}>
        {isChatbotReplay && <div className={styles['replay-separate']}></div>}
        <OfferGallery
          isChatbotReplay={isChatbotReplay}
          withReload={withReload}
          shareUrl={shareUrl}
          photoUrl={photoUrl}
          onMouseDown={handleMouseDown}
          onClick={onClick}
          onReload={onReload}
        />
        {withReload && onReload && (!Array.isArray(photoUrl) || photoUrl.length === 0) && (
          <div onClick={onReload} className={styles['reload-button']}>
            <IconActionReload16 color="icon-inverted-default" />
          </div>
        )}
        <a
          onMouseDown={handleMouseDown}
          href={shareUrl}
          className={classNames(styles['content'], isChatbotReplay && styles['content-replay'])}
          target="_blank"
          rel="noreferrer"
        >
          <span className={styles['price']} data-name="OfferMessage_price">
            <UIHeading5
              color={isOutcoming && !isChatbotReplay ? 'text-inverted-default' : 'control-main-primary-default'}
            >
              {formattedPrice}
            </UIHeading5>
            <s
              className={classNames(
                styles['formatted_old_price'],
                isOutcoming && !isChatbotReplay && styles['formatted_old_price_outcoming'],
              )}
            >
              <UIText2 color={isOutcoming && !isChatbotReplay ? 'white_60' : 'text-secondary-default'}>
                {formattedOldPrice}
              </UIText2>
            </s>
          </span>
          <span className={styles['info']}>
            <UIText2
              color={isOutcoming && !isChatbotReplay ? 'text-inverted-default' : 'text-primary-default'}
              data-name="OfferMessage_info"
            >
              {formattedInfo}
            </UIText2>
            <UIText2
              color={isOutcoming && !isChatbotReplay ? 'text-inverted-default' : 'text-primary-default'}
              data-name="OfferMessage_address"
            >
              {formattedAddress}
            </UIText2>
          </span>
        </a>
      </div>

      {formattedText && (
        <div className={styles['text_block']}>
          <span data-name="ChatMessageLayout_text">
            <UIText1 color={isOutcoming ? 'text-inverted-default' : 'text-primary-default'}>{formattedText}</UIText1>
          </span>
        </div>
      )}

      {isChatbotChat && !isOutcoming && isExpertButtonShow && (
        <div className={styles['chatbot-button-container']}>
          <OnboardingTooltip
            open={visibleTooltip}
            portalElement={tooltipPortal.current}
            placement="bottom-start"
            theme={'blue'}
            onClose={() => setCloseTooltip()}
            content={
              <div className={styles['tooltip-content']}>
                <img className={styles['tooltip-image']} src={avatar} alt="Фото" data-testid="tooltip-image" />
                <span>Попросите помощника кратко описать объект</span>
              </div>
            }
          >
            <div className={classNames(styles['chatbot-button'])} onClick={onExpertButton}>
              <Image width={16} height={16} objectFit="cover" src={avatar} data-testid="image" />
              <UIText1 color={'text-main-default'}>{expertButtonContent?.title}</UIText1>
            </div>
          </OnboardingTooltip>
          <div className={styles['like-button']} onClick={onLike}>
            {reaction === 'like' ? (
              <IconSocialLikeOn16 color="icon-main-default" />
            ) : (
              <IconSocialLikeOff16 color="icon-main-default" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};
